import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('admin_token');
};

// Create axios instance with auth header
const createAuthHeader = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Loan Programs API
export const getLoanPrograms = async () => {
  try {
    const response = await axios.get(`${API}/loan-programs`);
    return response.data;
  } catch (error) {
    console.error('Error fetching loan programs:', error);
    throw error;
  }
};

export const getLoanProgram = async (id) => {
  try {
    const response = await axios.get(`${API}/loan-programs/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching loan program:', error);
    throw error;
  }
};

// Testimonials API
export const getTestimonials = async () => {
  try {
    const response = await axios.get(`${API}/testimonials`);
    return response.data;
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    throw error;
  }
};

export const submitTestimonial = async (testimonialData) => {
  try {
    const response = await axios.post(`${API}/testimonials`, testimonialData);
    return response.data;
  } catch (error) {
    console.error('Error submitting testimonial:', error);
    throw error;
  }
};

// Stats API
export const getStats = async () => {
  try {
    const response = await axios.get(`${API}/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching stats:', error);
    throw error;
  }
};

// Loan Inquiries API
export const submitLoanInquiry = async (inquiryData) => {
  try {
    const response = await axios.post(`${API}/loan-inquiries`, inquiryData);
    return response.data;
  } catch (error) {
    console.error('Error submitting loan inquiry:', error);
    throw error;
  }
};

export const getLoanInquiryStatus = async (id) => {
  try {
    const response = await axios.get(`${API}/loan-inquiries/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inquiry status:', error);
    throw error;
  }
};

// Contact API
export const submitContactMessage = async (contactData) => {
  try {
    const response = await axios.post(`${API}/contact`, contactData);
    return response.data;
  } catch (error) {
    console.error('Error submitting contact message:', error);
    throw error;
  }
};

// Team Members API (Public)
export const getTeamMembers = async () => {
  try {
    const response = await axios.get(`${API}/team`);
    return response.data;
  } catch (error) {
    console.error('Error fetching team members:', error);
    throw error;
  }
};

// Admin Authentication API
export const adminLogin = async (credentials) => {
  try {
    const response = await axios.post(`${API}/admin/login`, credentials);
    // Store token in localStorage
    if (response.data.access_token) {
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin_username', response.data.username);
    }
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const adminLogout = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_username');
};

export const verifyAdminToken = async () => {
  try {
    const response = await axios.get(`${API}/admin/verify`, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error verifying token:', error);
    throw error;
  }
};

export const changeAdminPassword = async (passwordData) => {
  try {
    const response = await axios.put(`${API}/admin/change-password`, passwordData, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

// Admin Team Management API
export const getTeamMembersAdmin = async () => {
  try {
    const response = await axios.get(`${API}/admin/team`, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching team members (admin):', error);
    throw error;
  }
};

export const uploadTeamImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/admin/upload-image`, formData, {
      headers: {
        ...createAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const createTeamMember = async (memberData) => {
  try {
    const response = await axios.post(`${API}/admin/team`, memberData, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating team member:', error);
    throw error;
  }
};

export const updateTeamMember = async (id, memberData) => {
  try {
    const response = await axios.put(`${API}/admin/team/${id}`, memberData, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating team member:', error);
    throw error;
  }
};

export const deleteTeamMember = async (id) => {
  try {
    const response = await axios.delete(`${API}/admin/team/${id}`, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting team member:', error);
    throw error;
  }
};

// Website Images API
export const getWebsiteImagesPublic = async () => {
  try {
    const response = await axios.get(`${API}/website-images`);
    return response.data;
  } catch (error) {
    console.error('Error fetching website images:', error);
    throw error;
  }
};

export const getWebsiteImagesAdmin = async () => {
  try {
    const response = await axios.get(`${API}/admin/website-images`, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching website images (admin):', error);
    throw error;
  }
};

export const updateWebsiteImage = async (imageId, imageData) => {
  try {
    const response = await axios.put(`${API}/admin/website-images/${imageId}`, imageData, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating website image:', error);
    throw error;
  }
};


// Blog Posts API (Public)
export const getBlogPosts = async (category = null) => {
  try {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    const response = await axios.get(`${API}/blog${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

export const getBlogPost = async (slug) => {
  try {
    const response = await axios.get(`${API}/blog/${slug}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

// Blog Posts API (Admin)
export const getBlogPostsAdmin = async () => {
  try {
    const response = await axios.get(`${API}/admin/blog`, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching blog posts (admin):', error);
    throw error;
  }
};

export const createBlogPost = async (postData) => {
  try {
    const response = await axios.post(`${API}/admin/blog`, postData, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

export const updateBlogPost = async (id, postData) => {
  try {
    const response = await axios.put(`${API}/admin/blog/${id}`, postData, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

export const deleteBlogPost = async (id) => {
  try {
    const response = await axios.delete(`${API}/admin/blog/${id}`, {
      headers: createAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};
